import React from 'react';
import { Image, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import SimpleHeader from '../../components/SimpleHeader';
import TabBar from '../../components/TabBar';
import { COLORS } from '../../theme/themeTokens';
import type { V2Request } from '../../types/v2';
import { numberToAmountDisplay } from '../../utils/amountFormat';

interface Props {
  request: V2Request;
  onBack: () => void;
  onReceipt: (request: V2Request) => void;
  onRepeat: (request: V2Request) => void;
  onSupport: (request: V2Request) => void;
  onNavigate: (screen: string) => void;
}

const repeatableTypes = new Set(['withdraw', 'card_topup', 'current_to_deposit', 'deposit_to_current']);
const outgoingTypes = new Set(['withdraw', 'card_topup', 'current_to_deposit']);

/**
 * Requests that ask for something rather than move money: a card, a T-FUND
 * place, a personal investment. They carry no amount, no fee and no accounts,
 * so From said the client's own login back to them, To said "New card",
 * Amount a dash and Fee a zero — four rows that told them nothing. What is
 * left is the request itself: its number, where it has got to, and when it
 * was made.
 *
 * `open_deposit` is not one of these despite the name — it has both an amount
 * and an account to take it from.
 */
const applicationTypes = new Set(['open_card', 'open_t_fund', 'personal_investment']);

const formatDecimal = (value: string | null | undefined, maxFractional = 2) => {
  const formatted = numberToAmountDisplay(value ?? '0', maxFractional);
  return formatted.includes(',')
    ? formatted.replace(/0+$/, '').replace(/,$/, '')
    : formatted;
};

const formatDate = (value: string) => new Date(value).toLocaleString('en-GB', {
  day: '2-digit', month: '2-digit', year: 'numeric',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
}).replace(',', '').replace(/\//g, '.');

const OrderDetailScreen: React.FC<Props> = ({ request, onBack, onReceipt, onRepeat, onSupport, onNavigate }) => {
  const source = request.source ?? (request.currency ? `Current · ${request.currency}` : '—');
  const destination = request.destination ?? request.destination_address ?? '—';
  const amountSign = outgoingTypes.has(request.type_code) ? '−' : '';
  const amount = request.amount
    ? `${amountSign}${formatDecimal(request.amount)} ${request.currency ?? ''}`.trim()
    : '—';
  // fee_percent is a fraction ("0.005" = 0,5 %). The fee itself is charged in
  // fee_currency — the sell token, or USDT when a stock sale pays it out of
  // the proceeds.
  const feePercent = Number(request.fee_percent ?? 0) * 100;
  const feeCurrency = request.fee_currency ?? request.currency ?? '';
  const feeParts = feePercent > 0 ? [`${formatDecimal(String(feePercent))}%`] : [];
  feeParts.push(`${formatDecimal(request.fee, 8)} ${feeCurrency}`.trim());
  // No "Total balance" row. It only ever appeared on exchanges, and there the
  // fee sits inside the amount — the sum locked on the sell account is the
  // amount, so the row repeated Amount verbatim. The Fee row says how much of
  // that amount goes to the fee.
  const isApplication = applicationTypes.has(request.type_code);
  const rows = isApplication
    ? [
      ['ID', String(request.number).padStart(8, '0')],
      ['Progress', request.status_name],
      ['Date', formatDate(request.created_at)],
    ]
    : [
      ['ID', String(request.number).padStart(8, '0')],
      ['Progress', request.status_name],
      ['From', source],
      ['To', destination],
      ['Amount', amount],
      ['Date', formatDate(request.created_at)],
      ['Fee', feeParts.join('  ')],
    ];

  return (
    <View style={styles.screen}>
      <SimpleHeader title={request.type_name} onBack={onBack} />
      <ScrollView contentContainerStyle={styles.content}>
        <Text style={styles.sectionTitle}>Details</Text>
        <View style={styles.card}>
          {rows.map(([label, value]) => (
            <View key={label} style={styles.row}>
              <Text style={styles.label}>{label}</Text>
              <Text style={styles.value}>{value}</Text>
            </View>
          ))}
          {!!request.cancellation_reason && (
            <View style={styles.reason}>
              <Text style={styles.label}>Cancellation reason</Text>
              <Text style={[styles.value, styles.reasonText]}>{request.cancellation_reason}</Text>
            </View>
          )}
        </View>
        {/* A receipt is drawn from the transaction the request settled into,
            and an application settles into none — the button could only ever
            answer "no completed transaction is linked to this order". */}
        {(request.status_code === 'confirmed' && !isApplication) && (
          <View style={styles.actions}>
            <TouchableOpacity style={styles.secondaryButton} activeOpacity={0.8} onPress={() => onReceipt(request)}>
              <Text style={styles.secondaryText}>Receipt</Text>
            </TouchableOpacity>
            {repeatableTypes.has(request.type_code) && (
              <TouchableOpacity style={styles.primaryButton} activeOpacity={0.8} onPress={() => onRepeat(request)}>
                <Text style={styles.primaryText}>Repeat</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
        <TouchableOpacity style={styles.supportButton} activeOpacity={0.8} onPress={() => onSupport(request)}>
          <Image source={require('../../assets/support.png')} style={styles.supportIcon} />
          <Text style={styles.supportText}>If you have any questions -{'\n'}<Text style={styles.underlined}>write to support</Text></Text>
        </TouchableOpacity>
      </ScrollView>
      <TabBar activeTab={null} onNavigate={onNavigate} />
    </View>
  );
};

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: COLORS.pureLight },
  content: { padding: 18, paddingBottom: 100 },
  sectionTitle: { fontSize: 16, color: COLORS.primaryNavy, marginBottom: 12 },
  card: { backgroundColor: COLORS.white, borderWidth: 1, borderColor: COLORS.softGrey, borderRadius: 12, padding: 15 },
  row: { minHeight: 47, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 14 },
  label: { color: COLORS.blueSteel, fontSize: 14 },
  value: { flex: 1, color: COLORS.primaryNavy, fontSize: 14, textAlign: 'right' },
  reason: { borderTopWidth: 1, borderTopColor: COLORS.softGrey, paddingTop: 12, marginTop: 4, gap: 8 },
  reasonText: { color: '#BE3244', textAlign: 'left' },
  actions: { flexDirection: 'row', gap: 12, marginTop: 18 },
  secondaryButton: { flex: 1, height: 51, borderWidth: 1, borderColor: COLORS.primaryNavy, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  primaryButton: { flex: 1, height: 51, borderRadius: 9, backgroundColor: COLORS.primaryNavy, alignItems: 'center', justifyContent: 'center' },
  secondaryText: { color: COLORS.primaryNavy, fontSize: 16 },
  primaryText: { color: COLORS.white, fontSize: 16 },
  supportButton: { minHeight: 54, borderWidth: 1, borderColor: COLORS.primaryNavy, borderRadius: 10, flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, marginTop: 18 },
  supportIcon: { width: 23, height: 23, tintColor: COLORS.primaryNavy, marginRight: 13 },
  supportText: { color: COLORS.primaryNavy, fontSize: 14 },
  underlined: { textDecorationLine: 'underline' },
});

export default OrderDetailScreen;
